import React, { useEffect, useState } from 'react';
import { API_URL_All } from '../../api';
import * as general from '../../libs/general';
import './OrderProductPickerModal.css';

const buildImageUrl = (path) => {
  if (!path) return '';
  if (String(path).startsWith('http://') || String(path).startsWith('https://')) return path;
  return `${API_URL_All}/${path}`;
};

const OrderProductPickerModal = ({
  show,
  keyword,
  customerCode,
  rows,
  loadingList,
  locked,
  allowMultiple = false,
  onClose,
  onSearch,
  onChooseRow,
  onChooseRows,
}) => {
  const [selectedByKey, setSelectedByKey] = useState({});
  const [applying, setApplying] = useState(false);
  /** Giá trị ô tìm kiếm giữ ở local để gõ tiếng Việt (IME) không bị giật khi parent đang tải kết quả. */
  const [inputValue, setInputValue] = useState(keyword ?? '');

  useEffect(() => {
    if (!show) {
      setSelectedByKey({});
      setApplying(false);
      return;
    }
    setInputValue(keyword ?? '');
  }, [show]);

  useEffect(() => {
    if (!show || inputValue === (keyword ?? '')) return undefined;
    const timer = setTimeout(() => onSearch(inputValue), 250);
    return () => clearTimeout(timer);
  }, [inputValue, show]);

  if (!show) return null;

  const selectedRows = Object.values(selectedByKey);

  const toggleRow = (row) => {
    if (locked || applying) return;
    setSelectedByKey((prev) => {
      const next = { ...prev };
      if (next[row.rowKey]) delete next[row.rowKey];
      else next[row.rowKey] = row;
      return next;
    });
  };

  const confirmSelection = async () => {
    if (!allowMultiple || applying || locked || selectedRows.length === 0) return;
    setApplying(true);
    try {
      await onChooseRows?.(selectedRows);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="order-product-picker-popup"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="order-product-picker-surface"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="order-product-picker-header">
          <div>
            <h5 className="mb-1">Chọn sản phẩm</h5>
            <div className="small text-muted">
              Mã khách hàng: <strong>{customerCode || '—'}</strong>
              {allowMultiple ? ' · Có thể chọn nhiều sản phẩm' : ''}
            </div>
          </div>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Đóng"></button>
        </div>

        <div className="order-product-picker-toolbar">
          <input
            type="text"
            className="form-control"
            placeholder="Tìm theo mã hoặc tên sản phẩm..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
          />
        </div>

        <div className="order-product-picker-table-wrap">
          <table className="table table-hover table-bordered align-middle mb-0 order-product-picker-table">
            <thead>
              <tr>
                <th className="text-center" style={{ width: '42px' }}> </th>
                <th style={{ width: '92px' }}>Ảnh</th>
                <th className="text-center" style={{ width: '160px' }}>Mã</th>
                <th>Tên sản phẩm</th>
                <th className="text-center" style={{ width: '140px' }}>Giá</th>
                <th className="text-center" style={{ width: '120px' }}>Khối lượng</th>
                <th className="text-center" style={{ width: '160px' }}>Kích thước</th>
              </tr>
            </thead>
            <tbody>
              {loadingList ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">Đang tải danh sách sản phẩm...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">Không có sản phẩm phù hợp.</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const imageUrl = buildImageUrl(row.imagePath);
                  const selected = Boolean(selectedByKey[row.rowKey]);
                  return (
                    <tr
                      key={row.rowKey}
                      className={selected || row.isCurrent ? 'table-active order-product-picker-row-selected' : ''}
                      style={{ cursor: locked || applying ? 'default' : 'pointer' }}
                      onClick={() => {
                        if (locked || applying) return;
                        if (allowMultiple) {
                          toggleRow(row);
                          return;
                        }
                        onChooseRow(row);
                      }}
                    >
                      <td className="text-center">
                        {allowMultiple ? (
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={locked || applying}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleRow(row)}
                            aria-label={`Chọn ${row.name || 'sản phẩm'}`}
                          />
                        ) : (
                          row.isCurrent ? '●' : '○'
                        )}
                      </td>
                      <td>
                        <div className="order-product-picker-thumb">
                          {imageUrl ? (
                            <img src={imageUrl} alt={row.name || 'product'} />
                          ) : (
                            <div className="order-product-picker-thumb-fallback">No image</div>
                          )}
                        </div>
                      </td>
                      <td className="small text-center">
                        <div>{row.code || '—'}</div>
                        {row.subCode ? <div className="text-muted">{row.subCode}</div> : null}
                      </td>
                      <td>
                        <div className="">{row.name || 'Không có tên'}</div>
                        {row.subName ? <div className="small text-muted">{row.subName}</div> : null}
                      </td>
                      <td className="text-center">{general.formatNumber(row.price)}</td>
                      <td className="text-center">{row.weight ?? '—'}</td>
                      <td className="text-center">{row.dimensions || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {allowMultiple && (
          <div className="order-product-picker-footer">
            <div className="order-product-picker-selected">
              <span className="small text-muted flex-shrink-0">Đã chọn {selectedRows.length} sản phẩm{selectedRows.length > 0 ? ':' : ''}</span>
              {selectedRows.map((row) => (
                <button
                  key={row.rowKey}
                  type="button"
                  className="order-product-picker-chip"
                  title={`Bấm để bỏ chọn: ${row.name || 'Không có tên'}${row.subName ? ` · ${row.subName}` : ''}`}
                  disabled={locked || applying}
                  onClick={() => toggleRow(row)}
                >
                  {row.name || 'Không có tên'}{row.subName ? ` · ${row.subName}` : ''}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={locked || applying || selectedRows.length === 0}
              onClick={confirmSelection}
            >
              Thêm sản phẩm đã chọn{selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderProductPickerModal;
